import { service } from '@ember/service';
import type ExtendedStoreService from '@triptyk/ember-utils/services/extended-store';
import { ProductChangeset } from 'back-office/changesets/product';
import type ProductModel from 'back-office/models/product';
import type { ChangesetService } from 'common/services/changesets/changeset-service';
import type StoreService from '@ember-data/store';
import type IntlService from 'ember-intl/services/intl';
import type FlashMessageService from 'ember-cli-flash/services/flash-messages';
import type ErrorHandlerService from 'common/services/error-handler';
import BaseActionProductService from './base-action-product';
import { saveFile } from 'back-office/utils/save-file';
import type { Relation } from 'back-office/utils/file-to-form';
import { fileToFormData } from 'back-office/utils/file-to-form';
import type TestimonyModel from 'back-office/models/testimony';
import type { TestimonyDTO } from 'back-office/changesets/product';
import type { SyncHasMany } from '@ember-data/model';
import type TestimonyModelCommon from 'common/models/testimony';
import type TestimonyImageService from 'back-office/services/testimony-image';

export default class ProductService
  extends BaseActionProductService
  implements ChangesetService<ProductChangeset>
{
  @service declare store: StoreService;
  @service declare extendedStore: ExtendedStoreService;
  @service declare intl: IntlService;
  @service declare flashMessages: FlashMessageService;
  @service declare errorHandler: ErrorHandlerService;
  @service declare testimonyImage: TestimonyImageService;

  async save(changeset: ProductChangeset): Promise<ProductModel> {
    changeset.execute();
    const changesetData = changeset.data;
    const product = this.extendedStore.peekOrCreateRecord(
      'product',
      changesetData.id ?? ''
    );
    const relation: Relation = {
      ressourceName: 'product',
      fieldName: 'product',
      id: product.id,
    };

    if (product == null) {
      throw new Error('model not found');
    }

    product.setProperties({
      status: changesetData.status,
      ogMetadata: {
        title: changesetData.ogMetadata?.title,
        description: changesetData.ogMetadata?.description,
        keywords: changesetData.ogMetadata?.keywords,
      },
      similarProducts: changesetData.similarProducts.map((product) =>
        this.store.peekRecord('product', product.id)
      ),
      tags: changesetData.tags.map((tag) =>
        this.store.peekRecord('tag', tag.id)
      ),
    });

    try {
      await changeset.save();
      await product.save();
      await Promise.all([
        await saveFile(
          product,
          changesetData.cover,
          await product.cover,
          this.cover,
          relation
        ),
        await saveFile(
          product,
          changesetData?.ogImage,
          await product.ogImage,
          this.ogImage,
          relation
        ),
        await saveFile(
          product,
          changesetData?.formatorImage,
          await product.formatorImage,
          this.formatorImage,
          relation
        ),
      ]);

      const existingImageIds = this.collectTestimonyImageIds(product);
      const testimonies = await this.saveTestimonies(
        product,
        changesetData.testimonies
      );
      await this.saveTestimonyImages(
        testimonies,
        changesetData.testimonies,
        existingImageIds
      );

      this.flashMessages.success(
        this.intl.t('components.pages.products.success_edit')
      );
    } catch (e: unknown) {
      const error = (await e) as AdapterError;
      this.errorHandler.handle(changeset, error.errors);
    }
    return product;
  }

  async changesetFrom(id: string) {
    let product = this.extendedStore.peekOrFail('product', id) as ProductModel;
    const changeset = new ProductChangeset({
      ...(await this.getCommonProperties(product)),
      type: product.type,
      similarProducts: this.getSimilarPojo(
        await product.get('similarProducts')
      ),
      testimonies: this.getTestimoniesPojo(product.testimonies),
    });
    return changeset;
  }

  private async saveTestimonies(
    product: ProductModel,
    dtos: TestimonyDTO[]
  ): Promise<TestimonyModel[]> {
    const keptIds = dtos.map((dto) => dto.id).filter(Boolean);

    for (const model of product.testimonies.slice()) {
      if (!keptIds.includes(model.id)) {
        await model.destroyRecord();
      }
    }

    const testimonies: TestimonyModel[] = [];

    for (const dto of dtos) {
      const testimony = this.extendedStore.peekOrCreateRecord(
        'testimony',
        dto.id
      ) as TestimonyModel;

      testimony.setProperties({
        firstName: dto.firstName,
        lastName: dto.lastName,
        role: dto.role,
        trainingYear: dto.trainingYear,
        content: dto.content,
        video: dto.video,
        product,
      });

      await testimony.save();
      testimonies.push(testimony);
    }

    return testimonies;
  }

  private collectTestimonyImageIds(product: ProductModel): Map<string, string> {
    const ids = new Map<string, string>();

    for (const testimony of product.testimonies.slice()) {
      const imageId = testimony.belongsTo('image').id();
      if (testimony.id && imageId) {
        ids.set(testimony.id, imageId);
      }
    }

    return ids;
  }

  private async saveTestimonyImages(
    testimonies: TestimonyModel[],
    dtos: TestimonyDTO[],
    existingImageIds: Map<string, string>
  ) {
    for (const [index, testimony] of testimonies.entries()) {
      const file = dtos[index]?.image;
      const relation: Relation = {
        ressourceName: 'testimony',
        fieldName: 'testimony',
        id: testimony.id,
      };
      const imageId = file?.id ?? existingImageIds.get(testimony.id);
      const formData =
        file &&
        fileToFormData(
          {
            ...file,
            id: imageId,
          },
          relation
        );

      if (formData) {
        await this.testimonyImage.save(formData);
        continue;
      }

      if (file === undefined) {
        const existingId = existingImageIds.get(testimony.id);
        const image = existingId
          ? this.store.peekRecord('testimony-image', existingId)
          : testimony.image;
        await image?.destroyRecord();
      }
    }
  }

  private getTestimoniesPojo(
    models: SyncHasMany<TestimonyModelCommon>
  ): TestimonyDTO[] {
    return models
      .map((testimony) => ({
        id: testimony.id,
        firstName: testimony.firstName,
        lastName: testimony.lastName,
        role: testimony.role,
        trainingYear: testimony.trainingYear,
        content: testimony.content,
        video: testimony.video,
        image: this.testimonyImage.getFilePojo(testimony.image ?? undefined),
      }));
  }
}

declare module '@ember/service' {
  interface Registry {
    'changesets/product': ProductService;
  }
}
